import type {
  EquipmentCategory,
  EquipmentObjectType,
  BuiltInEquipmentProcessType,
  EquipmentProcessType,
  EquipmentStyle,
} from './equipmentTypes';

export const EQUIPMENT_LAYER_VERSION = '1.0.0';

/** Extensible list of Digital Twin equipment process types. */
export const EQUIPMENT_PROCESS_TYPES: BuiltInEquipmentProcessType[] = [
  'PHOTO',
  'PHOTO_MET',
  'ETCH',
  'CVD',
  'PVD',
  'ALD',
  'DIFFUSION',
  'IMPLANT',
  'CMP',
  'CLEAN',
  'MET',
];

export const DEFAULT_PROCESS_TYPE: EquipmentProcessType = 'PHOTO';

/** Map retired / external labels onto the current process-type vocabulary. */
export const LEGACY_PROCESS_TYPE_ALIASES: Record<string, EquipmentProcessType> = {
  METROLOGY: 'MET',
  CUSTOM: 'PHOTO',
  LITHO: 'PHOTO',
};

export const EQUIPMENT_PROCESS_TYPE_LABELS: Record<BuiltInEquipmentProcessType, string> = {
  PHOTO: 'PHOTO',
  PHOTO_MET: 'PHOTO_MET',
  ETCH: 'ETCH',
  CVD: 'CVD',
  PVD: 'PVD',
  ALD: 'ALD',
  DIFFUSION: 'DIFFUSION',
  IMPLANT: 'IMPLANT',
  CMP: 'CMP',
  CLEAN: 'CLEAN',
  MET: 'MET',
};

/** Canvas nameplate / label ink — one process tool mesh, types distinguished by label. */
export const EQUIPMENT_PROCESS_TYPE_LABEL_COLORS: Record<
  BuiltInEquipmentProcessType,
  string
> = {
  PHOTO: '#1d4ed8',
  PHOTO_MET: '#2563eb',
  ETCH: '#7c3aed',
  CVD: '#0d9488',
  PVD: '#0891b2',
  ALD: '#0284c7',
  DIFFUSION: '#c2410c',
  IMPLANT: '#9333ea',
  CMP: '#4f46e5',
  CLEAN: '#059669',
  MET: '#ca8a04',
};

export function equipmentProcessTypeLabelColor(value: unknown): string {
  const normalized = normalizeEquipmentProcessType(value);
  return (
    EQUIPMENT_PROCESS_TYPE_LABEL_COLORS[normalized as BuiltInEquipmentProcessType] ??
    '#111827'
  );
}

export function normalizeEquipmentProcessType(value: unknown): EquipmentProcessType {
  const raw = String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^A-Z0-9_]/g, '');
  if (!/[A-Z0-9]/.test(raw)) return DEFAULT_PROCESS_TYPE;
  if ((EQUIPMENT_PROCESS_TYPES as readonly string[]).includes(raw)) {
    return raw as EquipmentProcessType;
  }
  return LEGACY_PROCESS_TYPE_ALIASES[raw] ?? raw;
}

export function equipmentProcessTypeLabel(value: unknown): string {
  const normalized = normalizeEquipmentProcessType(value);
  return EQUIPMENT_PROCESS_TYPE_LABELS[normalized as BuiltInEquipmentProcessType] ?? normalized;
}

/** Default FOUP capacity for newly created stockers. */
export const DEFAULT_FOUP_CAPACITY = 100;

/** Default chamber capacity for a standard four-port equipment tool. */
export const DEFAULT_TOOL_FOUP_CAPACITY = 2;

/** Default process wait for equipment tools, stored as wall seconds. */
export const DEFAULT_PROCESS_WAIT_SEC = 60;

/**
 * Stocker dwell so the OHT is still at the port when PICKUP/DROP stamps.
 * Stored as wall seconds (live payload sends value × simSpeed).
 */
export const DEFAULT_STOCKER_PROCESS_WAIT_SEC = 12;

/** Map legacy category → Digital Twin process type. */
export function categoryToProcessType(category: EquipmentCategory): EquipmentProcessType {
  switch (category) {
    case 'Photolithography':
      return 'PHOTO';
    case 'etch':
      return 'ETCH';
    case 'CMP':
      return 'CMP';
    case 'CVD':
      return 'CVD';
    case 'PVD':
      return 'PVD';
    case 'implant':
      return 'IMPLANT';
    case 'diffusion':
      return 'DIFFUSION';
    default:
      return DEFAULT_PROCESS_TYPE;
  }
}

export function processTypeToCategory(processType: EquipmentProcessType): EquipmentCategory {
  switch (processType) {
    case 'PHOTO':
    case 'PHOTO_MET':
      return 'Photolithography';
    case 'ETCH':
      return 'etch';
    case 'CMP':
      return 'CMP';
    case 'CVD':
      return 'CVD';
    case 'PVD':
      return 'PVD';
    case 'IMPLANT':
      return 'implant';
    case 'DIFFUSION':
      return 'diffusion';
    default:
      return 'custom';
  }
}

export const OBJECT_TYPES: EquipmentObjectType[] = [
  'equipment',
  'stocker',
  'buffer',
  'lift',
  'utility',
  'room',
  'other',
];

export const OBJECT_TYPE_LABELS: Record<EquipmentObjectType, string> = {
  equipment: 'Equipment',
  stocker: 'Stocker',
  buffer: 'Buffer',
  lift: 'Lift',
  utility: 'Utility',
  room: 'Room',
  other: 'Other',
};

export const EQUIPMENT_CATEGORIES: EquipmentCategory[] = [
  'Photolithography',
  'etch',
  'implant',
  'PVD',
  'CVD',
  'diffusion',
  'CMP',
  'custom',
];

export const CATEGORY_LABELS: Record<EquipmentCategory, string> = {
  Photolithography: 'Photolithography',
  etch: 'Etch',
  implant: 'Implant',
  PVD: 'PVD',
  CVD: 'CVD',
  diffusion: 'Diffusion',
  CMP: 'CMP',
  custom: 'Custom',
};

/** Default accent colour per object type. */
export const OBJECT_TYPE_COLORS: Record<EquipmentObjectType, string> = {
  equipment: '#0f2744',
  stocker: '#64748b',
  buffer: '#0369a1',
  lift: '#334155',
  utility: '#0e7490',
  room: '#475569',
  other: '#1e293b',
};

/** Default glyph per object type. */
export const OBJECT_TYPE_ICONS: Record<EquipmentObjectType, string> = {
  equipment: '🛠',
  stocker: '📦',
  buffer: '🗄',
  lift: '🛗',
  utility: '⚡',
  room: '🏠',
  other: '🔷',
};

/** Suggested glyph per process category (used when defaulting templates). */
export const CATEGORY_ICONS: Record<EquipmentCategory, string> = {
  Photolithography: '🔬',
  etch: '🧪',
  implant: '⚛️',
  PVD: '💧',
  CVD: '🌫️',
  diffusion: '🔥',
  CMP: '🪞',
  custom: '⚙️',
};

export function defaultStyleForType(type: EquipmentObjectType): EquipmentStyle {
  return {
    color: OBJECT_TYPE_COLORS[type] ?? OBJECT_TYPE_COLORS.equipment,
    fillOpacity: 0.14,
    icon: '',
  };
}
